/**
 * Cart Fees System
 * Automatically adds/updates/removes service fees and travel insurance based on cart contents
 */

class CartFees {
  constructor() {
    // Service Fee Settings (20€ per person with 19% VAT)
    this.SERVICE_FEE_AMOUNT = 2000; // 20€ in cents
    this.SERVICE_FEE_VARIANT_ID = null;
    this.SERVICE_FEE_ENABLED = false;

    // Travel Insurance Settings (5% of cart total per person)
    this.TRAVEL_INSURANCE_RATE = 5.0; // 5%
    this.TRAVEL_INSURANCE_PACKAGES = {
      'V25': { minRange: 0, maxRange: 500, price: 2500, variantId: null, productHandle: null }, // €25
      'V28': { minRange: 501, maxRange: 600, price: 2800, variantId: null, productHandle: null }, // €28
      'V31': { minRange: 601, maxRange: 700, price: 3100, variantId: null, productHandle: null }, // €31
      'V35': { minRange: 701, maxRange: 800, price: 3500, variantId: null, productHandle: null }, // €35
      'V39': { minRange: 801, maxRange: 900, price: 3900, variantId: null, productHandle: null }, // €39
      'V45': { minRange: 901, maxRange: 1000, price: 4500, variantId: null, productHandle: null }, // €45
      'V55': { minRange: 1001, maxRange: 1300, price: 5500, variantId: null, productHandle: null } // €55
    };
    this.TRAVEL_INSURANCE_MIN_TOTAL = 0;
    this.TRAVEL_INSURANCE_ENABLED = false;

    this.isUpdating = false;
    this.isCleaningProperties = false; // Flag to prevent recursive property cleanup
    this.needsRecheck = false; // Flag to indicate a change was missed during update
    this.currentServiceFeeQuantity = 0;
    this.currentTravelInsuranceAmount = 0;

    // Make the instance globally available for shuttle calculator to access
    window.cartFees = this;
  }

  async init() {
    // Get service fee settings from theme settings
    const serviceFeeVariantIdStr = window.theme.serviceFee?.variantId || '';
    this.SERVICE_FEE_VARIANT_ID = serviceFeeVariantIdStr ? parseInt(serviceFeeVariantIdStr, 10) : null;
    this.SERVICE_FEE_AMOUNT = window.theme.serviceFee?.amount || 2000;
    this.SERVICE_FEE_ENABLED = window.theme.serviceFee?.enabled || false;

    // Service fee initialized

    // Get travel insurance settings from theme settings
    this.TRAVEL_INSURANCE_MIN_TOTAL = window.theme.travelInsurance?.minCartTotal || 0;
    this.TRAVEL_INSURANCE_RATE = (window.theme.travelInsurance?.rate || 5.0) / 100; // Convert percentage to decimal
    this.TRAVEL_INSURANCE_ENABLED = window.theme.travelInsurance?.enabled || false;

    // Load insurance package settings
    if (window.theme.travelInsurance?.packages) {
      Object.keys(this.TRAVEL_INSURANCE_PACKAGES).forEach(packageCode => {
        const packageKey = packageCode.toLowerCase(); // v25, v28, etc.
        const packageSettings = window.theme.travelInsurance.packages[packageKey];
        if (packageSettings) {
          this.TRAVEL_INSURANCE_PACKAGES[packageCode].productHandle = packageSettings.productHandle;
          this.TRAVEL_INSURANCE_PACKAGES[packageCode].variantId = packageSettings.variantId;
        }
      });
    }

    // Auto-get variant IDs from product handles if not provided
    if (this.SERVICE_FEE_ENABLED && !this.SERVICE_FEE_VARIANT_ID && window.theme.serviceFee?.productHandle) {
      this.SERVICE_FEE_VARIANT_ID = await this.getDefaultVariantId(window.theme.serviceFee.productHandle);
    }

    // Auto-get variant IDs for insurance packages if not provided
    if (this.TRAVEL_INSURANCE_ENABLED) {
      for (const packageCode of Object.keys(this.TRAVEL_INSURANCE_PACKAGES)) {
        const packageData = this.TRAVEL_INSURANCE_PACKAGES[packageCode];
        if (!packageData.variantId && packageData.productHandle) {
          packageData.variantId = await this.getDefaultVariantId(packageData.productHandle);
        }
      }
    }

    // Debug output
    console.log('Cart Insurance Initialization:', {
      SERVICE_FEE_ENABLED: this.SERVICE_FEE_ENABLED,
      SERVICE_FEE_VARIANT_ID: this.SERVICE_FEE_VARIANT_ID,
      TRAVEL_INSURANCE_ENABLED: this.TRAVEL_INSURANCE_ENABLED,
      TRAVEL_INSURANCE_PACKAGES: this.TRAVEL_INSURANCE_PACKAGES,
      cartStoreAvailable: !!window.theme?.cart?.store,
      themeSettings: {
        serviceFee: window.theme.serviceFee,
        travelInsurance: window.theme.travelInsurance
      }
    });

    if (!this.SERVICE_FEE_ENABLED && !this.TRAVEL_INSURANCE_ENABLED) {
      console.log('All cart fees are disabled');
      return;
    }

    if (this.SERVICE_FEE_ENABLED && !this.SERVICE_FEE_VARIANT_ID) {
      console.warn('Service fee variant ID not configured');
    }

    if (this.TRAVEL_INSURANCE_ENABLED) {
      const missingPackages = Object.keys(this.TRAVEL_INSURANCE_PACKAGES).filter(code =>
        !this.TRAVEL_INSURANCE_PACKAGES[code].variantId
      );
      if (missingPackages.length > 0) {
        console.warn('Travel insurance packages missing variant IDs:', missingPackages);
      }
    }

    // Subscribe to cart changes
    this.unsubscribe = window.theme.cart.store.subscribe((state, prevState) => {
      if (this.isUpdating || this.isCleaningProperties) {
        console.log('[Service Fee] Subscription - update/cleanup in progress, will recheck after');
        // Don't block completely - set flag to recheck after current update finishes
        this.needsRecheck = true;
        return;
      }

      console.log('[Service Fee] Cart store subscription triggered');

      // Check if cart actually changed (including items, prices, or properties)
      const itemCountChanged = state.products.item_count !== prevState.products.item_count;
      const itemsLengthChanged = state.products.items.length !== prevState.products.items.length;
      const priceChanged = state.products.total_price !== prevState.products.total_price;

      // Also check if item properties changed (deep comparison of cart items)
      const itemsStringified = JSON.stringify(state.products.items);
      const prevItemsStringified = JSON.stringify(prevState.products.items);
      const propertiesChanged = itemsStringified !== prevItemsStringified;

      console.log('[Service Fee] Change detection:', {
        itemCountChanged,
        itemsLengthChanged,
        priceChanged,
        propertiesChanged
      });

      // ALWAYS verify service fees on any cart change to ensure they're in sync
      if (itemCountChanged || itemsLengthChanged || priceChanged || propertiesChanged) {
        console.log('[Service Fee] Cart changed - triggering handleCartChange');
        // Handle service fees when cart changes
        this.handleCartChange(state, prevState);
      }
    });
  }

  async cleanupExcessPersonProperties(cart) {
    // Prevent recursive cleanup during updates
    if (this.isCleaningProperties) {
      console.log('[Service Fee] Cleanup already in progress, skipping');
      return;
    }

    console.log('[Service Fee] Checking for excess person properties...');

    for (const item of cart.items) {
      // Skip service fees and insurance
      if (item.variant_id === this.SERVICE_FEE_VARIANT_ID || this.isInsuranceVariant(item.variant_id)) {
        continue;
      }

      if (!item.properties || Object.keys(item.properties).length === 0) {
        continue;
      }

      // Count person properties
      const personNumbers = new Set();
      const allProperties = {};

      Object.keys(item.properties).forEach(key => {
        const personMatch = key.match(/^person[_\s-]?(\d+)[_\s-]?(.+)$/i);
        if (personMatch) {
          const personNumber = parseInt(personMatch[1], 10);
          personNumbers.add(personNumber);
        }
        allProperties[key] = item.properties[key];
      });

      const personCount = personNumbers.size;

      // Check if this is a ticket (quantity matches person count) or accommodation (quantity is room count)
      // For accommodations: quantity = 1 caravan, but personCount = 2+ persons
      // For tickets: quantity = 2 tickets, personCount = 2 persons
      const isLikelyTicket = item.product_title && (
        item.product_title.toLowerCase().includes('ticket') ||
        item.product_title.toLowerCase().includes('pass')
      );

      // Only cleanup excess persons for TICKETS when quantity is reduced
      // Never cleanup for accommodations (where quantity = room count, not person count)
      if (isLikelyTicket && personCount > item.quantity) {
        console.warn(`[Service Fee] ⚠️ Cleaning up excess person properties for ${item.product_title}`);
        console.warn(`[Service Fee] → Person count: ${personCount}, Quantity: ${item.quantity}`);
        console.warn(`[Service Fee] → Removing person_${item.quantity + 1} through person_${personCount}`);

        // Build new properties object without excess persons
        const cleanedProperties = {};
        Object.keys(allProperties).forEach(key => {
          const personMatch = key.match(/^person[_\s-]?(\d+)[_\s-]?(.+)$/i);
          if (personMatch) {
            const personNumber = parseInt(personMatch[1], 10);
            // Only keep person properties up to item.quantity
            if (personNumber <= item.quantity) {
              cleanedProperties[key] = allProperties[key];
            }
          } else {
            // Keep non-person properties
            cleanedProperties[key] = allProperties[key];
          }
        });

        // Update cart item with cleaned properties
        this.isCleaningProperties = true;
        try {
          await this.updateCartItemProperties(item, cleanedProperties);
          console.log(`[Service Fee] ✓ Cleaned up person properties for ${item.product_title}`);
        } catch (error) {
          console.error('[Service Fee] Failed to cleanup person properties:', error);
        } finally {
          this.isCleaningProperties = false;

          // Check if we need to recheck due to changes during cleanup
          if (this.needsRecheck) {
            console.log('[Service Fee] Recheck needed after cleanup - triggering update');
            this.needsRecheck = false;
            setTimeout(() => this.updateFees(), 150);
          }
        }
      }
    }
  }

  async updateCartItemProperties(item, newProperties) {
    // Shopify doesn't have a direct API to update properties
    // We need to remove the item and re-add it with new properties
    const line = await this.getLineNumber(item.key);
    if (!line) {
      console.error('Could not find line number for item:', item.key);
      return;
    }

    console.log('[Service Fee] Updating cart item properties via remove + re-add...');

    // Remove old item
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: 0 })
    });

    // Re-add with new properties
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: item.variant_id,
        quantity: item.quantity,
        properties: newProperties
      })
    });

    // Wait for cart to update
    await new Promise(resolve => setTimeout(resolve, 200));

    // Force cart store refresh to update UI immediately
    if (window.theme?.cart?.store) {
      console.log('[Service Fee] Refreshing cart store to update UI...');
      const freshCart = await this.fetchCart();

      // Update the cart store state - this will trigger all UI subscriptions
      // Use the same pattern as cart-shuttle-calculator.js
      if (typeof window.theme.cart.store.setState === 'function') {
        window.theme.cart.store.setState(state => ({
          ...state,
          products: freshCart
        }));
        console.log('[Service Fee] ✓ Cart store state updated via setState');
      }

      // Also dispatch cart:updated event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: freshCart }
      }));

      console.log('[Service Fee] ✓ Cart UI should update instantly now');
    }
  }

  async getLineNumber(itemKey) {
    const cartData = await this.fetchCart();
    const index = cartData.items.findIndex(item => item.key === itemKey);
    return index >= 0 ? index + 1 : null;
  }

  async getDefaultVariantId(productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const product = await response.json();
      if (product.variants && product.variants.length > 0) {
        return product.variants[0].id;
      }
    } catch (error) {
      console.error(`Failed to get variant ID for product ${productHandle}:`, error);
    }
    return null;
  }

  /**
   * Public method to explicitly update fees when needed
   */
  async updateFees() {
    console.log('Explicit fee update requested');
    if (!window.theme || !window.theme.cart || !window.theme.cart.store) {
      console.error('Cart store not available');
      return;
    }

    const cartState = window.theme.cart.store.getState();
    await this.handleCartChange(cartState, cartState);
    return true;
  }

  async handleCartChange(state, prevState) {
    // Fetch fresh cart to ensure we have the latest data
    let cartData;
    try {
      cartData = await this.fetchCart();
    } catch (error) {
      console.error('[Service Fee] Failed to fetch fresh cart:', error);
      cartData = state.products;
    }
    
    const cartAnalysis = this.analyzeCart(cartData);

    console.log('Cart change detected:', {
      uniqueGuestCount: cartAnalysis.ticketCount,
      totalExcludingFees: cartAnalysis.totalExcludingFees,
      SERVICE_FEE_ENABLED: this.SERVICE_FEE_ENABLED,
      hasServiceFee: !!cartAnalysis.serviceFeeItem,
      serviceFeeQty: cartAnalysis.serviceFeeItem?.quantity || 0
    });

    // FIRST: Clean up excess person properties if quantity was reduced
    await this.cleanupExcessPersonProperties(cartData);

    // Handle service fee - ALWAYS AUTO-ADD based on person count
    if (this.SERVICE_FEE_ENABLED && this.SERVICE_FEE_VARIANT_ID) {
      await this.handleServiceFee(cartAnalysis);
    }

    // NEVER automatically add travel insurance - only update UI
    // Only handle existing insurance (for quantity updates) if it's already in cart
    if (this.TRAVEL_INSURANCE_ENABLED && cartAnalysis.travelInsuranceItem) {
      // Only update quantity of existing insurance, never add new
      const currentPackageVariantId = cartAnalysis.travelInsuranceItem?.variant_id;
      const requiredQuantity = cartAnalysis.ticketCount;

      if (requiredQuantity <= 0) {
        // Remove insurance if ticket count is zero
        await this.removeTravelInsurance(cartAnalysis.travelInsuranceItem.key);
      } else if (cartAnalysis.travelInsuranceItem.quantity !== requiredQuantity) {
        // Only update quantity if different (don't change package type)
        await this.updateTravelInsuranceQuantity(cartAnalysis.travelInsuranceItem.key, requiredQuantity);
      }
    }

    // Trigger insurance recommendation update
    this.triggerInsuranceUpdate();

    // Notify UI components of insurance updates
    this.notifyInsuranceUpdate();
  }

  triggerInsuranceUpdate() {
    // Dispatch a custom event to notify insurance recommendation to update
    document.dispatchEvent(new CustomEvent('cart:fees:updated', {
      detail: {
        cartFees: this,
        insuranceInfo: this.getTravelInsuranceInfo()
      }
    }));
  }

  analyzeCart(cart) {
    const analysis = {
      totalExcludingFees: 0,
      ticketCount: 0,
      serviceFeeItem: null,
      travelInsuranceItem: null,
      itemBreakdown: [], // Track each item's contribution
      uniqueGuests: new Map() // Track unique guests by email across ALL items
    };

    console.log(`[Service Fee] Analyzing cart with ${cart.items.length} items`);

    cart.items.forEach(item => {
      if (item.variant_id === this.SERVICE_FEE_VARIANT_ID) {
        analysis.serviceFeeItem = item;
        console.log('[Service Fee] Found existing service fee item:', {
          quantity: item.quantity,
          line_price: item.line_price
        });
      } else if (this.isInsuranceVariant(item.variant_id)) {
        analysis.travelInsuranceItem = item;
        console.log('[Service Fee] Found travel insurance item');
      } else {
        // Regular product - count towards total
        analysis.totalExcludingFees += item.line_price;

        // Extract unique guests from this item and add to global set
        const guestsFromItem = this.extractGuestsFromCartItem(item);
        guestsFromItem.forEach(guest => {
          if (guest.email && !analysis.uniqueGuests.has(guest.email.toLowerCase())) {
            analysis.uniqueGuests.set(guest.email.toLowerCase(), guest);
          }
        });

        analysis.itemBreakdown.push({
          title: item.product_title || item.title,
          variant: item.variant_title,
          quantity: item.quantity,
          guestsFound: guestsFromItem.length,
          line_price: item.line_price
        });
      }
    });

    // Final ticket count = unique guests count
    analysis.ticketCount = analysis.uniqueGuests.size;

    console.log('[Service Fee] Cart analysis completed:', {
      uniqueGuestCount: analysis.ticketCount,
      uniqueEmails: Array.from(analysis.uniqueGuests.keys()),
      totalExcludingFees: analysis.totalExcludingFees,
      hasServiceFee: !!analysis.serviceFeeItem,
      serviceFeeQuantity: analysis.serviceFeeItem ? analysis.serviceFeeItem.quantity : 0,
      itemBreakdown: analysis.itemBreakdown
    });

    return analysis;
  }

  /**
   * Extract all guests from a cart item's properties
   * Returns array of guest objects with email as unique identifier
   */
  extractGuestsFromCartItem(item) {
    const guests = [];
    
    if (!item.properties || Object.keys(item.properties).length === 0) {
      return guests;
    }

    // Group properties by person number
    const personData = {};
    
    Object.keys(item.properties).forEach(key => {
      const value = item.properties[key];
      
      // Skip empty values and internal properties
      if (!value || value === '' || key.startsWith('_')) {
        return;
      }

      // Match person_X_property pattern
      const personMatch = key.match(/^person[_\s-]?(\d+)[_\s-]?(.+)$/i);
      if (personMatch) {
        const personNumber = parseInt(personMatch[1], 10);
        const propertyName = personMatch[2].toLowerCase().replace(/[\s-]/g, '_');
        
        if (!personData[personNumber]) {
          personData[personNumber] = {};
        }
        personData[personNumber][propertyName] = value;
      }
    });

    // Convert to array of guests with email as identifier
    Object.values(personData).forEach(person => {
      if (person.email) {
        guests.push({
          email: person.email,
          firstName: person.first_name || person.firstname || '',
          lastName: person.last_name || person.lastname || '',
          birthdate: person.birthdate || '',
          gender: person.gender || ''
        });
      }
    });

    return guests;
  }

  async handleServiceFee(analysis) {
    const requiredQuantity = analysis.ticketCount;
    const currentQuantity = analysis.serviceFeeItem ? analysis.serviceFeeItem.quantity : 0;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('[Service Fee] handleServiceFee CALLED');
    console.log('[Service Fee] Required Quantity:', requiredQuantity);
    console.log('[Service Fee] Current Quantity:', currentQuantity);
    console.log('[Service Fee] Has Service Fee Item:', !!analysis.serviceFeeItem);
    console.log('[Service Fee] isUpdating flag:', this.isUpdating);
    console.log('[Service Fee] Needs Update:', currentQuantity !== requiredQuantity);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // If no tickets, remove service fee
    if (requiredQuantity === 0) {
      if (analysis.serviceFeeItem) {
        console.log('[Service Fee] ➜ REMOVING service fee - no tickets');
        await this.removeServiceFee(analysis.serviceFeeItem.key);
      }
      return;
    }

    // Compare actual cart quantity with required quantity instead of internal counter
    if (currentQuantity !== requiredQuantity) {
      if (analysis.serviceFeeItem) {
        console.log(`[Service Fee] ➜ UPDATING service fee quantity from ${currentQuantity} to ${requiredQuantity}`);
        await this.updateServiceFee(analysis.serviceFeeItem.key, requiredQuantity);
      } else {
        console.log(`[Service Fee] ➜ ADDING service fee with quantity ${requiredQuantity}`);
        await this.addServiceFee(requiredQuantity);
      }
    } else {
      console.log('[Service Fee] ✓ Service fee quantity already correct:', currentQuantity);
    }
  }

  async handleTravelInsurance(analysis) {
    // Calculate 5% per person
    const totalAmountForInsurance = this.calculateTravelInsuranceAmount(analysis.totalExcludingFees);
    const amountPerPerson = analysis.ticketCount > 0 ? totalAmountForInsurance / analysis.ticketCount : 0;

    // If cart is empty or below minimum, remove travel insurance
    if (analysis.totalExcludingFees <= this.TRAVEL_INSURANCE_MIN_TOTAL || analysis.ticketCount === 0) {
      if (analysis.travelInsuranceItem) {
        await this.removeTravelInsurance(analysis.travelInsuranceItem.key);
      }
      return;
    }

    // Determine which insurance package to use
    const requiredPackage = this.determineInsurancePackage(amountPerPerson);

    if (!requiredPackage || !requiredPackage.variantId) {
      console.log('No suitable insurance package found or variant ID missing');
      if (analysis.travelInsuranceItem) {
        await this.removeTravelInsurance(analysis.travelInsuranceItem.key);
      }
      return;
    }

    // Check if we need different insurance package or quantity
    const currentPackageVariantId = analysis.travelInsuranceItem?.variant_id;
    const requiredQuantity = analysis.ticketCount;

    if (analysis.travelInsuranceItem) {
      // Check if package type or quantity changed
      if (currentPackageVariantId !== requiredPackage.variantId ||
        analysis.travelInsuranceItem.quantity !== requiredQuantity) {
        console.log('Updating insurance package from', currentPackageVariantId, 'to', requiredPackage.variantId);
        await this.updateTravelInsurance(analysis.travelInsuranceItem.key, requiredPackage, requiredQuantity);
      }
    } else {
      // Add new insurance
      console.log('Adding insurance package', requiredPackage.code, 'for', requiredQuantity, 'persons');
      await this.addTravelInsurance(requiredPackage, requiredQuantity);
    }
  }

  calculateTravelInsuranceAmount(cartTotal) {
    if (cartTotal <= this.TRAVEL_INSURANCE_MIN_TOTAL) return 0;
    return Math.round(cartTotal * this.TRAVEL_INSURANCE_RATE);
  }

  /**
   * Determines which insurance package to use based on the calculated amount per person
   * @param {number} amountPerPerson - The calculated 5% amount per person in cents
   * @returns {Object|null} - The insurance package object or null if no suitable package
   */
  determineInsurancePackage(amountPerPerson) {
    // Convert cents to euros for range comparison
    const amountInEuros = Math.round(amountPerPerson / 100);

    console.log('Determining insurance package for amount per person:', {
      amountPerPerson: amountPerPerson + ' cents',
      amountInEuros: amountInEuros + '€'
    });

    // Find the package that matches the amount range
    for (const [code, packageData] of Object.entries(this.TRAVEL_INSURANCE_PACKAGES)) {
      if (amountInEuros >= packageData.minRange && amountInEuros <= packageData.maxRange) {
        console.log('Selected insurance package:', code, packageData);
        return { code, ...packageData };
      }
    }

    console.log('No suitable insurance package found for amount:', amountInEuros + '€');
    return null;
  }

  /**
   * Check if a variant ID belongs to any of the insurance packages
   * @param {number} variantId - The variant ID to check
   * @returns {boolean} - True if it's an insurance variant
   */
  isInsuranceVariant(variantId) {
    return Object.values(this.TRAVEL_INSURANCE_PACKAGES).some(packageData =>
      packageData.variantId && packageData.variantId.toString() === variantId.toString()
    );
  }

  async addServiceFee(quantity) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.currentServiceFeeQuantity = quantity;

    try {
      await window.theme.cart.store.getState().add({
        id: this.SERVICE_FEE_VARIANT_ID,
        quantity: quantity,
        properties: {
          '_service_fee': 'true',
          '_per_person': quantity
        }
      });

      this.showNotification('service_fee_added', quantity);

      // Trigger insurance update to notify cart drawer
      this.notifyInsuranceUpdate();
    } catch (error) {
      console.error('Failed to add service fee:', error);
    } finally {
      this.isUpdating = false;

      // Check if we need to recheck due to changes during update
      if (this.needsRecheck) {
        console.log('[Service Fee] Recheck needed - triggering update');
        this.needsRecheck = false;
        setTimeout(() => this.updateFees(), 100);
      }
    }
  }

  async updateServiceFee(key, quantity) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.currentServiceFeeQuantity = quantity;

    try {
      await window.theme.cart.store.getState().change(key, quantity);
      this.showNotification('service_fee_updated', quantity);

      // Trigger insurance update to notify cart drawer
      this.notifyInsuranceUpdate();
    } catch (error) {
      console.error('Failed to update service fee:', error);
    } finally {
      this.isUpdating = false;

      // Check if we need to recheck due to changes during update
      if (this.needsRecheck) {
        console.log('[Service Fee] Recheck needed - triggering update');
        this.needsRecheck = false;
        setTimeout(() => this.updateFees(), 100);
      }
    }
  }

  async removeServiceFee(key) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.currentServiceFeeQuantity = 0;

    try {
      await window.theme.cart.store.getState().change(key, 0);
      this.showNotification('service_fee_removed', 0);

      // Trigger insurance update to notify cart drawer
      this.notifyInsuranceUpdate();
    } catch (error) {
      console.error('Failed to remove service fee:', error);
    } finally {
      this.isUpdating = false;

      // Check if we need to recheck due to changes during update
      if (this.needsRecheck) {
        console.log('[Service Fee] Recheck needed - triggering update');
        this.needsRecheck = false;
        setTimeout(() => this.updateFees(), 100);
      }
    }
  }

  async addTravelInsurance(insurancePackage, quantity) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.currentTravelInsuranceAmount = insurancePackage.price * quantity;

    try {
      console.log('Adding travel insurance package:', {
        packageCode: insurancePackage.code,
        variantId: insurancePackage.variantId,
        pricePerPerson: insurancePackage.price + ' cents (€' + (insurancePackage.price / 100).toFixed(2) + ')',
        quantity: quantity,
        totalAmount: (insurancePackage.price * quantity) + ' cents (€' + (insurancePackage.price * quantity / 100).toFixed(2) + ')'
      });

      await window.theme.cart.store.getState().add({
        id: insurancePackage.variantId,
        quantity: quantity,
        properties: {
          '_travel_insurance': 'true',
          '_insurance_package': insurancePackage.code,
          '_per_person_price': insurancePackage.price,
          '_persons_covered': quantity
        }
      });

      this.showNotification('travel_insurance_added', insurancePackage.price * quantity);
    } catch (error) {
      console.error('Failed to add travel insurance:', error);
    } finally {
      this.isUpdating = false;
    }
  }



  async updateTravelInsurance(key, insurancePackage, quantity) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.currentTravelInsuranceAmount = insurancePackage.price * quantity;

    try {
      console.log('Updating travel insurance package:', {
        packageCode: insurancePackage.code,
        variantId: insurancePackage.variantId,
        pricePerPerson: insurancePackage.price + ' cents (€' + (insurancePackage.price / 100).toFixed(2) + ')',
        quantity: quantity,
        totalAmount: (insurancePackage.price * quantity) + ' cents (€' + (insurancePackage.price * quantity / 100).toFixed(2) + ')'
      });

      // Check if we need to change the variant (different package)
      const cartData = await this.fetchCart();
      const currentItem = cartData.items.find(item => item.key === key);

      if (currentItem && currentItem.variant_id !== insurancePackage.variantId) {
        // Need to remove current item and add new package
        console.log('Switching insurance package - removing current and adding new');
        await window.theme.cart.store.getState().change(key, 0); // Remove current
        await this.addTravelInsurance(insurancePackage, quantity); // Add new package
      } else {
        // Same package, just update quantity
        await window.theme.cart.store.getState().change(key, quantity);
      }

      this.showNotification('travel_insurance_updated', insurancePackage.price * quantity);
    } catch (error) {
      console.error('Failed to update travel insurance:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Update only the quantity of an existing travel insurance without changing the package
   * @param {string} key - Cart item key
   * @param {number} quantity - New quantity
   */
  async updateTravelInsuranceQuantity(key, quantity) {
    if (this.isUpdating) return;

    this.isUpdating = true;

    try {
      console.log('Updating travel insurance quantity only:', {
        key: key,
        quantity: quantity
      });

      // Just update quantity, don't change package
      await window.theme.cart.store.getState().change(key, quantity);

      this.notifyInsuranceUpdate();
    } catch (error) {
      console.error('Failed to update travel insurance quantity:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  async removeTravelInsurance(key = null) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.currentTravelInsuranceAmount = 0;

    try {
      // If no key provided, find the insurance item in cart
      if (!key) {
        const cartData = await this.fetchCart();
        const insuranceItem = cartData.items.find(item =>
          this.isInsuranceVariant(item.variant_id)
        );

        if (!insuranceItem) {
          console.log('No travel insurance item found in cart to remove');
          return;
        }

        key = insuranceItem.key;
        console.log('Found travel insurance item to remove:', key);
      }

      await window.theme.cart.store.getState().change(key, 0);
      this.showNotification('travel_insurance_removed', 0);
      console.log('Travel insurance removed successfully');
    } catch (error) {
      console.error('Failed to remove travel insurance:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  showNotification(action, value) {
    // Create a subtle notification about fee changes
    const notification = document.createElement('div');
    notification.className = 'cart-fees-notification';
    notification.innerHTML = this.getNotificationMessage(action, value);

    // Style the notification
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--color-bg-secondary);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      padding: 12px 16px;
      font-size: 0.9rem;
      z-index: 9999;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      transition: all 0.3s ease;
      opacity: 0;
      transform: translateY(-20px);
    `;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateY(0)';
    }, 10);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateY(-20px)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  getNotificationMessage(action, value) {
    switch (action) {
      case 'service_fee_added':
        return `💼 Service fee added: ${window.Shopify.formatMoney(value * this.SERVICE_FEE_AMOUNT, window.theme.money_format)} (${value} ${value === 1 ? 'person' : 'people'})`;
      case 'service_fee_updated':
        return `💼 Service fee updated: ${window.Shopify.formatMoney(value * this.SERVICE_FEE_AMOUNT, window.theme.money_format)} (${value} ${value === 1 ? 'person' : 'people'})`;
      case 'service_fee_removed':
        return `💼 Service fee removed`;
      case 'travel_insurance_added':
        return `🛡️ Travel insurance added: ${window.Shopify.formatMoney(value, window.theme.money_format)}`;
      case 'travel_insurance_updated':
        return `🛡️ Travel insurance updated: ${window.Shopify.formatMoney(value, window.theme.money_format)}`;
      case 'travel_insurance_removed':
        return `🛡️ Travel insurance removed`;
      default:
        return `Cart fees updated`;
    }
  }

  // Public methods for UI interaction
  async addTravelInsuranceManually() {
    const cartAnalysis = this.analyzeCart(window.theme.cart.store.getState().products);
    const totalAmountForInsurance = this.calculateTravelInsuranceAmount(cartAnalysis.totalExcludingFees);
    const amountPerPerson = cartAnalysis.ticketCount > 0 ? totalAmountForInsurance / cartAnalysis.ticketCount : 0;

    const requiredPackage = this.determineInsurancePackage(amountPerPerson);

    if (requiredPackage && requiredPackage.variantId && !cartAnalysis.travelInsuranceItem && cartAnalysis.ticketCount > 0) {
      await this.addTravelInsurance(requiredPackage, cartAnalysis.ticketCount);
      this.notifyInsuranceUpdate();
    }
  }

  async removeTravelInsuranceManually() {
    const cartAnalysis = this.analyzeCart(window.theme.cart.store.getState().products);

    if (cartAnalysis.travelInsuranceItem) {
      await this.removeTravelInsurance(cartAnalysis.travelInsuranceItem.key);
      this.notifyInsuranceUpdate();
    }
  }

  getTravelInsuranceInfo() {
    const cartState = window.theme.cart.store.getState();
    const cartAnalysis = this.analyzeCart(cartState.products);
    const totalAmountForInsurance = this.calculateTravelInsuranceAmount(cartAnalysis.totalExcludingFees);
    const amountPerPerson = cartAnalysis.ticketCount > 0 ? totalAmountForInsurance / cartAnalysis.ticketCount : 0;
    const recommendedPackage = this.determineInsurancePackage(amountPerPerson);

    const info = {
      hasInsurance: !!cartAnalysis.travelInsuranceItem,
      recommendedAmount: recommendedPackage ? recommendedPackage.price * cartAnalysis.ticketCount : 0,
      recommendedPackage: recommendedPackage,
      amountPerPerson: amountPerPerson,
      cartTotal: cartAnalysis.totalExcludingFees,
      ticketCount: cartAnalysis.ticketCount,
      enabled: this.TRAVEL_INSURANCE_ENABLED && !!recommendedPackage?.variantId
    };

    console.log('Travel insurance info:', info);
    return info;
  }

  // Notify UI components of insurance updates
  notifyInsuranceUpdate() {
    const info = this.getTravelInsuranceInfo();
    document.dispatchEvent(new CustomEvent('cart:insurance:updated', {
      detail: info
    }));
  }

  // Method to process service fees during cart updates (called from cart store)
  async processServiceFeesForCartUpdate(cartData) {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('[Service Fee] processServiceFeesForCartUpdate CALLED');
    console.log('[Service Fee] isUpdating:', this.isUpdating);
    console.log('[Service Fee] SERVICE_FEE_ENABLED:', this.SERVICE_FEE_ENABLED);
    console.log('[Service Fee] SERVICE_FEE_VARIANT_ID:', this.SERVICE_FEE_VARIANT_ID);

    if (this.isUpdating || !this.SERVICE_FEE_ENABLED || !this.SERVICE_FEE_VARIANT_ID) {
      console.log('[Service Fee] processServiceFeesForCartUpdate SKIPPED');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return null; // No changes needed
    }

    const cartAnalysis = this.analyzeCart(cartData);
    const currentQuantity = cartAnalysis.serviceFeeItem ? cartAnalysis.serviceFeeItem.quantity : 0;
    const requiredQuantity = cartAnalysis.ticketCount;

    console.log('[Service Fee] Processing service fees for cart update:', {
      currentQuantity,
      requiredQuantity,
      hasServiceFeeItem: !!cartAnalysis.serviceFeeItem,
      needsUpdate: currentQuantity !== requiredQuantity
    });
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    let needsUpdate = false;

    // Check if we need to add service fee
    if (requiredQuantity > 0 && !cartAnalysis.serviceFeeItem) {
      console.log('Adding service fee during cart update for', requiredQuantity, 'persons');
      needsUpdate = true;

      // Set updating flag to prevent subscription loops
      this.isUpdating = true;

      try {
        // Use direct Shopify API to avoid recursion
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: this.SERVICE_FEE_VARIANT_ID,
              quantity: requiredQuantity, // Use actual person count
              properties: {
                '_service_fee': 'true',
                '_for_persons': requiredQuantity.toString()
              }
            }]
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to add service fee: ${response.status}`);
        }

        // Fetch fresh cart data after service fee addition
        const updatedCart = await this.fetchCart();
        console.log('Service fee added with quantity', requiredQuantity);
        return updatedCart;

      } catch (error) {
        console.error('Failed to add service fee during cart update:', error);
        return null;
      } finally {
        this.isUpdating = false;
        // Check if we need to recheck due to changes during update
        if (this.needsRecheck) {
          console.log('[Service Fee] Recheck needed after add - triggering update');
          this.needsRecheck = false;
          setTimeout(() => this.updateFees(), 100);
        }
      }
    }

    // Check if we need to update service fee quantity - USE ACTUAL CART QUANTITY
    else if (requiredQuantity > 0 && cartAnalysis.serviceFeeItem &&
      currentQuantity !== requiredQuantity) {
      console.log('Updating service fee quantity during cart update from', currentQuantity, 'to', requiredQuantity);
      needsUpdate = true;

      this.isUpdating = true;

      try {
        // Use direct Shopify API to avoid recursion
        const line = cartData.items.findIndex(item => item.key === cartAnalysis.serviceFeeItem.key) + 1;
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: line,
            quantity: requiredQuantity  // Use actual required quantity
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to update service fee: ${response.status}`);
        }

        // Fetch fresh cart data after service fee update
        const updatedCart = await this.fetchCart();
        console.log('Service fee quantity updated to', requiredQuantity);
        return updatedCart;

      } catch (error) {
        console.error('Failed to update service fee during cart update:', error);
        return null;
      } finally {
        this.isUpdating = false;
        // Check if we need to recheck due to changes during update
        if (this.needsRecheck) {
          console.log('[Service Fee] Recheck needed after update - triggering update');
          this.needsRecheck = false;
          setTimeout(() => this.updateFees(), 100);
        }
      }
    }

    // Check if we need to remove service fee
    else if (requiredQuantity === 0 && cartAnalysis.serviceFeeItem) {
      console.log('Removing service fee during cart update (no tickets)');
      needsUpdate = true;

      this.isUpdating = true;

      try {
        // Use direct Shopify API to avoid recursion
        const line = cartData.items.findIndex(item => item.key === cartAnalysis.serviceFeeItem.key) + 1;
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: line,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to remove service fee: ${response.status}`);
        }

        // Fetch fresh cart data after service fee removal
        const updatedCart = await this.fetchCart();
        console.log('Service fee removed, fetched updated cart');
        return updatedCart;

      } catch (error) {
        console.error('Failed to remove service fee during cart update:', error);
        return null;
      } finally {
        this.isUpdating = false;
        // Check if we need to recheck due to changes during update
        if (this.needsRecheck) {
          console.log('[Service Fee] Recheck needed after remove - triggering update');
          this.needsRecheck = false;
          setTimeout(() => this.updateFees(), 100);
        }
      }
    }

    return null; // No changes needed
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      throw error;
    }
  }

  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }
}

// Initialize when cart store is available
document.addEventListener('DOMContentLoaded', () => {
  // Wait for cart store to be initialized
  const initFees = async () => {
    if (window.theme?.cart?.store) {
      const fees = new CartFees();
      await fees.init();
      window.theme.cart.fees = fees;

      // Initial notification to UI components
      setTimeout(() => {
        fees.notifyInsuranceUpdate();
      }, 100);

      // Trigger an explicit update to ensure service fees are correct on page load
      // This handles cases where items were added without fees (e.g. from booking flow bug)
      setTimeout(() => {
        fees.updateFees();
      }, 500);
    } else {
      setTimeout(initFees, 100);
    }
  };

  initFees();
});

// Export for manual initialization if needed
window.CartFees = CartFees; 